import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Bookmark, BookmarkCheck } from 'lucide-react';
import SEO from '@/components/SEO';

const LearnArticle = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    api.get(`/content/articles/${slug}`).then(({ data }) => setA(data));
    if (user) {
      api.get('/me/saved-articles').then(({ data }) => setIsSaved(!!data.find((x) => x.slug === slug))).catch(() => {});
    }
  }, [slug, user]);

  const toggleSave = async () => {
    if (!user) { toast.error('Sign in to save articles'); return; }
    try {
      if (isSaved) { await api.delete(`/me/saved-articles/${slug}`); setIsSaved(false); toast.success('Removed from saved'); }
      else { await api.post('/me/saved-articles', { slug }); setIsSaved(true); toast.success('Saved to your dashboard'); }
    } catch { toast.error('Failed'); }
  };

  if (!a) return <div className="sans-container sans-section text-sans_mute">Loading…</div>;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: a.title,
    description: a.excerpt,
    image: a.cover_image,
    datePublished: a.created_at,
    author: { '@type': 'Organization', name: 'Saans Ayurved' },
  };

  return (
    <>
      <SEO title={a.seo_title || a.title} description={a.seo_description || a.excerpt} image={a.cover_image} type="article" schema={schema} />
      <div className="bg-sans_bone">
        <section className="sans-section">
          <div className="sans-container max-w-3xl">
            <div className="flex justify-between items-center">
              <Link to="/learn" className="sans-label hover:text-sans_ink">← Knowledge Hub</Link>
              <button onClick={toggleSave} data-testid="article-save-btn" className="flex items-center gap-2 text-xs text-sans_ink hover:opacity-70">
                {isSaved ? <BookmarkCheck className="w-4 h-4" strokeWidth={1.5} /> : <Bookmark className="w-4 h-4" strokeWidth={1.5} />}
                {isSaved ? 'Saved' : 'Save'}
              </button>
            </div>

            <div className="sans-label mt-8 mb-4">{a.category} · {a.reading_time} min read</div>
            <h1 className="sans-h1">{a.title}</h1>
            <div className="sans-rule mt-8" />

            <div className="mt-12 aspect-[16/9] bg-sans_line overflow-hidden">
              <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover" />
            </div>

            <div className="mt-12 text-lg leading-relaxed text-sans_ink whitespace-pre-line font-serif">{a.body}</div>

            <div className="mt-16 border-t border-sans_line pt-10">
              <div className="sans-label mb-3">Go deeper</div>
              <h3 className="font-serif text-3xl text-sans_ink">Understand your own constitution.</h3>
              <p className="text-sans_mute mt-3 max-w-md">A five-minute assessment is a good place to begin.</p>
              <Link to="/assessment" className="sans-btn-primary mt-8">Start free assessment</Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LearnArticle;
